import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { X } from 'lucide-react';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';

/**
 * Multi-select with search + ArrowUp/ArrowDown/Enter (Enter toggles highlighted option).
 */
const SearchableMultiSelect = ({
  value = [],
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  label,
  onSearch,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchTerm.length >= 2 && onSearch) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          await onSearch(searchTerm);
        } finally {
          setLoading(false);
        }
      }, 300);
    }
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, isOpen, onSearch]);

  const selectedOptions = options.filter((opt) => value.includes(opt.Id));
  const displayText =
    selectedOptions.length > 0 ? `${selectedOptions.length} selected` : placeholder;

  const filteredOptions = options.filter((opt) => {
    if (searchTerm.length < 2) return true;
    return (opt.Name || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleToggle = (optionId) => {
    if (value.includes(optionId)) onChange(value.filter((id) => id !== optionId));
    else onChange([...value, optionId]);
  };

  const { highlightedIndex, setHighlightedIndex, onKeyDown, setItemRef } = useListKeyboardNav({
    isOpen: isOpen && !loading && filteredOptions.length > 0,
    itemCount: filteredOptions.length,
    onSelectIndex: (index) => {
      const option = filteredOptions[index];
      if (option) handleToggle(option.Id);
    },
    onClose: () => {
      setIsOpen(false);
      setSearchTerm('');
    },
    resetKey: searchTerm,
  });

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent text-left flex items-center justify-between ${
            disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'cursor-pointer'
          }`}
        >
          <span className={selectedOptions.length > 0 ? 'text-gray-900' : 'text-gray-400'}>
            {displayText}
          </span>
          <span className="text-gray-400">▼</span>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={onKeyDown}
                className="w-full px-3 py-2 border-0 border-b-2 border-gray-200 focus:outline-none focus:border-[#46955A] bg-transparent text-sm"
                autoFocus
              />
            </div>
            <div className="max-h-48 overflow-y-auto" role="listbox">
              {loading ? (
                <div className="px-3 py-4 text-center">
                  <CircularProgress size={20} sx={{ color: '#46955A' }} />
                </div>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <div
                      key={option.Id}
                      role="option"
                      aria-selected={value.includes(option.Id)}
                      ref={(node) => setItemRef(index, node)}
                      className={`flex items-center px-3 py-2 cursor-pointer ${
                        isHighlighted ? 'bg-[#f0f9f4]' : 'hover:bg-gray-50'
                      }`}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => handleToggle(option.Id)}
                    >
                      <input
                        type="checkbox"
                        checked={value.includes(option.Id)}
                        onChange={() => {}}
                        className="w-4 h-4 text-[#46955A] border-gray-300 rounded focus:ring-[#46955A]"
                      />
                      <span className="ml-3 text-sm text-gray-900">{option.Name}</span>
                    </div>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                  {searchTerm.length >= 2 ? 'No options found' : 'Type at least 2 characters to search'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedOptions.map((option) => (
            <span
              key={option.Id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
            >
              {option.Name}
              <button
                type="button"
                onClick={() => onChange(value.filter((id) => id !== option.Id))}
                className="text-green-600 hover:text-green-800"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchableMultiSelect;
